import type {
  FullTrackingGraphQL,
  TrackingGraphQL,
  TrackingSpeedEvents,
} from './Types';
import { gql } from '@apollo/client';

import type { TimestampInMillis, UUID } from '@cobliteam/common-types';

import graphQLRequester from '../../request/GraphQLRequester';
import TimeZone from '../../request/Timezone';
import { getDeviceType } from '../device/DeviceTransformer';

export class GrahpQLTrackingRequester {
  // TO BE REMOVED: it fetches all tracking info and a user might not have the permission
  // to read the speed event events, for example
  public getFullTrackingInfo = (
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean
  ): Promise<FullTrackingGraphQL> => {
    const operationName = 'getFullTrackingInfo';
    const fullQuery = `
      vehicleId
      drivers {
        name
        id
      }
      pathGeojson {
        type
        geometry {
          type
          coordinates
        }
        properties {
          timestamps
        }
      }
      roadSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
      }
      vehicleSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
      }
      stops {
        startTime
        endTime
        driverName
        idleOnInMillis
        idleOffInMillis
        hasFilteredIdleOnTime
        location {
          lat
          lng
        }
        geofences {
          name
          category
        }
      }
      summary {
        totalFuelCost
        totalDistance
        totalDuration
        totalStops
        totalSpeedEvents
      }
      incidents {
        type
        time
        latitude
        longitude
      }
      vehicle {
        id
        licensePlate
        brand
        model
        fuelConsumption
        fuelPrice
      }
      device {
        type
        cobliId
        lastLocation {
          isPlugged
          pluggedUpdateTimestamp
        }
      }
    `;
    return this.execQuery<FullTrackingGraphQL>(
      operationName,
      vehicleId,
      startTimestamp,
      endTimestamp,
      useMapMatch,
      fullQuery
    );
  };

  public getTrackingEssencialInfo = (
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean
  ): Promise<TrackingGraphQL> => {
    const operationName = 'getTrackingEssencialInfo';
    const fullQuery = `
      vehicleId
      pathGeojson {
        type
        geometry {
          type
          coordinates
        }
        properties {
          timestamps
        }
      }
      stops {
        startTime
        endTime
        driverName
        idleOnInMillis
        idleOffInMillis
        hasFilteredIdleOnTime
        location {
          lat
          lng
        }
        geofences {
          name
          category
        }
      }
      summary {
        totalFuelCost
        totalDistance
        totalDuration
        totalStops
      }
      incidents {
        type
        time
        latitude
        longitude
      }
      vehicle {
        id
        licensePlate
        brand
        model
        fuelConsumption
        fuelPrice
      }
      device {
        type
        cobliId
        lastLocation {
          isPlugged
          pluggedUpdateTimestamp
        }
      }
    `;
    return this.execQuery<TrackingGraphQL>(
      operationName,
      vehicleId,
      startTimestamp,
      endTimestamp,
      useMapMatch,
      fullQuery
    ).then((tracking) => ({
      ...tracking,
      device: tracking.device
        ? {
            ...tracking.device,
            type: getDeviceType(tracking.device.type),
          }
        : undefined,
    }));
  };

  public getTrackingSpeedEvents = (
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean
  ): Promise<TrackingSpeedEvents> => {
    const operationName = 'getTrackingEvents';
    const query = `
      roadSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
        startTime
        medianSpeedInKmh
        medianBasedSeverity
      }
      vehicleSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
        startTime
        medianSpeedInKmh
        medianBasedSeverity
      }
      geofenceEvents {
        id
        time
        lng
        lat
        eventType
        geofence {
            id
            name
            category
        }
      }
      summary {
        totalSpeedEvents
      }
    `;

    return this.execQuery<TrackingSpeedEvents>(
      operationName,
      vehicleId,
      startTimestamp,
      endTimestamp,
      useMapMatch,
      query
    );
  };

  private execQuery = <T>(
    operationName: string,
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean,
    query: string
  ): Promise<any> => {
    const timeZone = TimeZone.getTimezone();

    return graphQLRequester
      .query<{ vehicleTracking: T }>({
        query: gql`
          query ${operationName}($trackingInput: VehicleTrackingInput) {
            vehicleTracking(input: $trackingInput) {
              ${query}
            }
          }`,
        variables: {
          trackingInput: {
            vehicle_id: vehicleId,
            start_time: startTimestamp,
            end_time: endTimestamp,
            tz: timeZone,
            match: useMapMatch,
          },
        },
      })
      .then((graphQlResponse) => graphQlResponse.vehicleTracking);
  };
}

export default GrahpQLTrackingRequester;
